import { execFile, spawn, } from "node:child_process";
import { randomUUID } from "node:crypto";
import { readdir, readFile } from "node:fs/promises";
import { normalizeCommandEnvironment } from "./process-environment.js";
import { resolvePlatformCommand } from "./process-launch.js";
import { spawnWindowsJobProcess } from "./windows-process-job.js";
const PROCESS_TABLE_COLUMN_SEPARATOR = /\s+/u;
const PROCESS_TABLE_LINE_SEPARATOR = /\r?\n/u;
const PROCESS_SUPERVISION_ENV_KEY = "OPENMELD_PROCESS_SUPERVISION_ID";
const PROCESS_SUPERVISION_DISCOVERY_RETRY_DELAY_MS = 100;
const PROCESS_SUPERVISION_DISCOVERY_MAX_ATTEMPTS = 2;
const PROCESS_TABLE_STOP_PHASE_SNAPSHOT_MAX_AGE_MS = 75;
const PROCESS_DISCOVERY_DIAGNOSTIC_WINDOW_MS = 60_000;
export function createAgentControllerProcessTableSnapshotReader(input) {
    if (!(Number.isFinite(input.maxAgeMs) && input.maxAgeMs > 0)) {
        throw new Error("process table snapshot max age must be positive");
    }
    const now = input.now ?? Date.now;
    let snapshot = null;
    return async (options) => {
        const readAtMs = now();
        if (snapshot && !options?.fresh) {
            const ageMs = readAtMs - snapshot.capturedAtMs;
            if (snapshot.pending || (ageMs >= 0 && ageMs < input.maxAgeMs)) {
                return await snapshot.processTable;
            }
        }
        const processTable = input.readProcessTable();
        const currentSnapshot = {
            capturedAtMs: readAtMs,
            pending: true,
            processTable,
        };
        snapshot = currentSnapshot;
        try {
            return await processTable;
        }
        finally {
            currentSnapshot.pending = false;
        }
    };
}
export function createAgentControllerProcessDiscoveryDiagnosticAggregator(input) {
    const now = input?.now ?? Date.now;
    const windowMs = input?.windowMs ?? PROCESS_DISCOVERY_DIAGNOSTIC_WINDOW_MS;
    const write = input?.write ?? ((level, event, payload) => console[level](event, payload));
    const aggregates = new Map();
    return {
        failure: (failure) => {
            const observedAtMs = now();
            const error = normalizeErrorForDiagnostic(failure.error);
            const reasonCode = isProcessDiscoveryTimeoutError(failure.error)
                ? "process_discovery_timeout"
                : "process_discovery_failed";
            const key = `${failure.source}:${reasonCode}:${error.name}`;
            const aggregate = aggregates.get(key) ?? {
                affectedProcessIds: new Set(),
                error,
                firstAtMs: observedAtMs,
                lastAtMs: observedAtMs,
                lastReportedAtMs: Number.NEGATIVE_INFINITY,
                occurrenceCount: 0,
                reporterError: failure.reporterError
                    ? normalizeErrorForDiagnostic(failure.reporterError)
                    : null,
                source: failure.source,
            };
            aggregate.affectedProcessIds.add(failure.processId);
            aggregate.error = error;
            aggregate.lastAtMs = observedAtMs;
            aggregate.occurrenceCount += 1;
            aggregates.set(key, aggregate);
            if (observedAtMs - aggregate.lastReportedAtMs < windowMs) {
                return;
            }
            aggregate.lastReportedAtMs = observedAtMs;
            write("warn", "[openmeld.agent_controller.process_discovery_failed]", {
                affectedProcessIds: [...aggregate.affectedProcessIds].slice(0, 20),
                durationMs: observedAtMs - aggregate.firstAtMs,
                errorCode: error.code,
                errorMessage: error.message,
                errorName: error.name,
                occurrenceCount: aggregate.occurrenceCount,
                reasonCode,
                ...(aggregate.reporterError
                    ? {
                        reporterErrorMessage: aggregate.reporterError.message,
                        reporterErrorName: aggregate.reporterError.name,
                    }
                    : {}),
                signal: failure.signal,
                source: failure.source,
            });
        },
        recovered: (source) => {
            const recoveredAtMs = now();
            for (const [key, aggregate] of aggregates) {
                if (aggregate.source !== source) {
                    continue;
                }
                write("info", "[openmeld.agent_controller.process_discovery_recovered]", {
                    affectedProcessIds: [...aggregate.affectedProcessIds].slice(0, 20),
                    durationMs: recoveredAtMs - aggregate.firstAtMs,
                    occurrenceCount: aggregate.occurrenceCount,
                    reasonCode: "process_discovery_recovered",
                    source,
                });
                aggregates.delete(key);
            }
        },
    };
}
function isProcessDiscoveryTimeoutError(error) {
    if (!(error instanceof Error)) {
        return false;
    }
    const processError = error;
    return processError.code === "ETIMEDOUT" || processError.killed === true;
}
const processDiscoveryDiagnostics = createAgentControllerProcessDiscoveryDiagnosticAggregator();
export function spawnAgentControllerProcess(input) {
    const signalTarget = resolveAgentControllerProcessSignalTarget();
    const supervisionId = input.supervisionId ?? randomUUID();
    const env = {
        ...normalizeCommandEnvironment(input.env),
        [PROCESS_SUPERVISION_ENV_KEY]: supervisionId,
    };
    const invocation = resolvePlatformCommand(input.bin, input.args, env);
    if (process.platform === "win32") {
        const job = spawnWindowsJobProcess({ invocation, env, cwd: input.cwd });
        return {
            child: job.child,
            processId: job.child.pid ?? null,
            signalTarget,
            supervisionId,
            terminate: job.terminate,
            terminateRemainingDescendants: job.cleanup,
        };
    }
    const child = spawn(invocation.bin, invocation.args, {
        cwd: input.cwd,
        detached: signalTarget === "process_group",
        env: {
            ...env,
            [PROCESS_SUPERVISION_ENV_KEY]: supervisionId,
        },
        shell: input.shell,
        windowsVerbatimArguments: invocation.windowsVerbatimArguments,
        windowsHide: true,
        stdio: ["pipe", "pipe", "pipe"],
    });
    const processId = typeof child.pid === "number" ? child.pid : null;
    const trackedDescendantProcessIds = new Set();
    return {
        child,
        processId,
        signalTarget,
        supervisionId,
        terminate: (signal) => signalAgentControllerProcessTree({
            processId,
            signal,
            signalTarget,
            trackedDescendantProcessIds,
        }),
        terminateRemainingDescendants: () => terminateRemainingAgentControllerProcesses({
            processId,
            signalTarget,
            supervisionId,
            trackedDescendantProcessIds,
        }),
    };
}
export function resolveAgentControllerProcessSignalTarget(platform = process.platform) {
    return platform === "win32" ? "process" : "process_group";
}
export function resolveAgentControllerProcessSignalId(input) {
    return input.signalTarget === "process_group"
        ? -input.processId
        : input.processId;
}
export function signalAgentControllerProcessTree(input) {
    if (input.processId === null) {
        return false;
    }
    if (input.signalTarget === "process") {
        return signalProcessId({
            killProcess: input.killProcess,
            processId: input.processId,
            signal: input.signal,
        });
    }
    const trackedDescendantProcessIds = input.trackedDescendantProcessIds ?? new Set();
    let sent = false;
    const signalFailures = [];
    const attemptSignal = (processId) => {
        try {
            sent =
                signalProcessId({
                    killProcess: input.killProcess,
                    processId,
                    signal: input.signal,
                }) || sent;
        }
        catch (error) {
            signalFailures.push(error);
        }
    };
    attemptSignal(resolveAgentControllerProcessSignalId({
        processId: input.processId,
        signalTarget: input.signalTarget,
    }));
    for (const descendantProcessId of trackedDescendantProcessIds) {
        attemptSignal(descendantProcessId);
    }
    if (signalFailures.length > 0) {
        throw signalFailures[0];
    }
    return sent;
}
export function terminateRemainingAgentControllerProcesses(input) {
    return runRemainingAgentControllerProcessCleanup(input);
}
async function runRemainingAgentControllerProcessCleanup(input) {
    let sent = false;
    const trackedDescendantProcessIds = input.trackedDescendantProcessIds ?? new Set();
    for (let attempt = 1; attempt <= PROCESS_SUPERVISION_DISCOVERY_MAX_ATTEMPTS; attempt += 1) {
        let cleanupError = null;
        const discoveryError = await collectSupervisedProcessIds({
            listSupervisedProcessIds: input.listSupervisedProcessIds,
            processId: input.processId,
            reportDiscoveryFailure: input.reportDiscoveryFailure,
            signalTarget: input.signalTarget,
            supervisionId: input.supervisionId,
            trackedDescendantProcessIds,
        });
        try {
            sent =
                signalAgentControllerProcessTree({
                    killProcess: input.killProcess,
                    processId: input.processId,
                    signal: "SIGKILL",
                    signalTarget: input.signalTarget,
                    trackedDescendantProcessIds,
                }) || sent;
        }
        catch (error) {
            cleanupError = error;
            reportAgentControllerProcessCleanupFailure({
                failure: {
                    error,
                    processId: input.processId,
                    signal: "SIGKILL",
                },
                reporter: input.reportCleanupFailure,
            });
        }
        const terminalError = cleanupError ?? discoveryError;
        const reachedFinalAttempt = attempt >= PROCESS_SUPERVISION_DISCOVERY_MAX_ATTEMPTS;
        const cleanupSettled = discoveryError === null && (cleanupError !== null || sent);
        if (reachedFinalAttempt || cleanupSettled) {
            return terminalError === null
                ? { ok: true, signalSent: sent }
                : { error: terminalError, ok: false, signalSent: sent };
        }
        await (input.waitForDiscoveryRetry ?? waitForAgentControllerProcessDiscoveryRetry)();
    }
    return { ok: true, signalSent: sent };
}
async function collectSupervisedProcessIds(input) {
    if (input.signalTarget !== "process_group" || input.processId === null) {
        return null;
    }
    const listSupervisedProcessIds = input.listSupervisedProcessIds ?? listAgentControllerSupervisedProcessIds;
    try {
        const supervisedProcessIds = await listSupervisedProcessIds({
            processId: input.processId,
            supervisionId: input.supervisionId,
        });
        for (const descendantProcessId of supervisedProcessIds) {
            input.trackedDescendantProcessIds.add(descendantProcessId);
        }
        return null;
    }
    catch (error) {
        reportAgentControllerProcessDiscoveryFailure({
            failure: {
                error,
                processId: input.processId,
                signal: "SIGKILL",
                source: "supervision_marker",
            },
            reporter: input.reportDiscoveryFailure,
        });
        return error;
    }
}
function waitForAgentControllerProcessDiscoveryRetry() {
    return new Promise((resolve) => {
        setTimeout(resolve, PROCESS_SUPERVISION_DISCOVERY_RETRY_DELAY_MS);
    });
}
export const listAgentControllerDescendantProcessIds = async (processId) => {
    const processTable = await readProcessTable(["-axo", "pid=,ppid=,pgid="], {
        maxBuffer: 4 * 1024 * 1024,
    });
    processDiscoveryDiagnostics.recovered("descendant_tree");
    return resolveAgentControllerDescendantProcessIds({
        processId,
        processTable,
    });
};
const readMacOsSupervisedProcessTable = createAgentControllerProcessTableSnapshotReader({
    // One Service drain can stop several controllers in the same event-loop
    // turn. They need one system snapshot, not one blocking `ps eww` scan per
    // controller. Pending reads stay shared even when slow; settled snapshots
    // expire before the bounded 100 ms retry, which still receives a fresh read.
    maxAgeMs: PROCESS_TABLE_STOP_PHASE_SNAPSHOT_MAX_AGE_MS,
    readProcessTable: async () => await readProcessTable(["eww", "-axo", "pid=,ppid=,pgid=,command="], {
        maxBuffer: 32 * 1024 * 1024,
    }),
});
export const listAgentControllerSupervisedProcessIds = async (input) => {
    if (process.platform === "linux") {
        const processTable = await readProcessTable(["-e", "-o", "pid=,ppid=,pgid="], { maxBuffer: 4 * 1024 * 1024 });
        processDiscoveryDiagnostics.recovered("supervision_marker");
        return resolveAgentControllerSupervisedProcessIds({
            ...input,
            markedProcessIds: await listLinuxSupervisedProcessIds(input.supervisionId),
            processTable,
        });
    }
    const processTable = await readMacOsSupervisedProcessTable({
        fresh: input.fresh,
    });
    processDiscoveryDiagnostics.recovered("supervision_marker");
    return resolveAgentControllerSupervisedProcessIds({
        ...input,
        processTable,
    });
};
export function resolveAgentControllerSupervisedProcessIds(input) {
    return resolveAgentControllerProcessIdsFromTable({
        ...input,
        supervisionMarker: `${PROCESS_SUPERVISION_ENV_KEY}=${input.supervisionId}`,
    });
}
function resolveAgentControllerProcessIdsFromTable(input) {
    const childrenByParentProcessId = new Map();
    const processGroupByProcessId = new Map();
    const markedProcessIds = new Set(input.markedProcessIds);
    for (const line of input.processTable.split(PROCESS_TABLE_LINE_SEPARATOR)) {
        const [processIdText, parentProcessIdText, processGroupIdText, ...commandParts] = line.trim().split(PROCESS_TABLE_COLUMN_SEPARATOR);
        const processId = Number.parseInt(processIdText ?? "", 10);
        const parentProcessId = Number.parseInt(parentProcessIdText ?? "", 10);
        const processGroupId = Number.parseInt(processGroupIdText ?? "", 10);
        if (!Number.isInteger(processId) ||
            processId <= 0 ||
            !Number.isInteger(parentProcessId) ||
            parentProcessId < 0 ||
            !Number.isInteger(processGroupId) ||
            processGroupId <= 0) {
            continue;
        }
        const children = childrenByParentProcessId.get(parentProcessId) ?? [];
        children.push(processId);
        childrenByParentProcessId.set(parentProcessId, children);
        processGroupByProcessId.set(processId, processGroupId);
        if (input.supervisionMarker !== undefined &&
            commandParts.includes(input.supervisionMarker)) {
            markedProcessIds.add(processId);
        }
    }
    if (input.processId === null) {
        return [...markedProcessIds];
    }
    return resolveOutOfGroupAgentControllerProcessIds({
        childrenByParentProcessId,
        markedProcessIds,
        processGroupByProcessId,
        processId: input.processId,
    });
}
async function listLinuxSupervisedProcessIds(supervisionId) {
    const supervisionMarker = `${PROCESS_SUPERVISION_ENV_KEY}=${supervisionId}`;
    const processIds = [];
    for (const entry of await readdir("/proc", { withFileTypes: true })) {
        if (!entry.isDirectory()) {
            continue;
        }
        const processId = Number.parseInt(entry.name, 10);
        if (!(Number.isInteger(processId) && String(processId) === entry.name)) {
            continue;
        }
        try {
            const environment = await readFile(`/proc/${entry.name}/environ`, "utf8");
            if (environment.split("\0").includes(supervisionMarker)) {
                processIds.push(processId);
            }
        }
        catch (error) {
            if (!isExpectedLinuxProcessInspectionError(error)) {
                throw error;
            }
        }
    }
    return processIds;
}
function readProcessTable(args, input) {
    return new Promise((resolve, reject) => {
        execFile("ps", [...args], {
            encoding: "utf8",
            maxBuffer: input.maxBuffer,
            timeout: 2000,
        }, (error, stdout) => {
            if (error) {
                reject(error);
                return;
            }
            resolve(stdout);
        });
    });
}
function isExpectedLinuxProcessInspectionError(error) {
    if (!(error instanceof Error && "code" in error)) {
        return false;
    }
    const code = String(error.code);
    return (code === "EACCES" ||
        code === "ENOENT" ||
        code === "EPERM" ||
        code === "ESRCH");
}
function resolveOutOfGroupAgentControllerProcessIds(input) {
    const processIds = new Set();
    const pending = [
        ...(input.childrenByParentProcessId.get(input.processId) ?? []),
    ];
    const visited = new Set([input.processId]);
    while (pending.length > 0) {
        const descendantProcessId = pending.shift();
        if (descendantProcessId === undefined || visited.has(descendantProcessId)) {
            continue;
        }
        visited.add(descendantProcessId);
        if (input.processGroupByProcessId.get(descendantProcessId) !== input.processId) {
            processIds.add(descendantProcessId);
        }
        pending.push(...(input.childrenByParentProcessId.get(descendantProcessId) ?? []));
    }
    for (const markedProcessId of input.markedProcessIds) {
        if (markedProcessId !== input.processId &&
            input.processGroupByProcessId.get(markedProcessId) !== input.processId) {
            processIds.add(markedProcessId);
        }
    }
    return [...processIds];
}
export function resolveAgentControllerDescendantProcessIds(input) {
    return resolveAgentControllerProcessIdsFromTable(input);
}
function signalProcessId(input) {
    try {
        (input.killProcess ?? process.kill)(input.processId, input.signal);
        return true;
    }
    catch (error) {
        if (isNoSuchProcessError(error) ||
            isUnsignalableExitedProcessGroup(error, input.processId)) {
            return false;
        }
        throw error;
    }
}
function isUnsignalableExitedProcessGroup(error, processId) {
    // Darwin reports EPERM while an isolated process group contains only its
    // already-exited, not-yet-reaped leader. OpenMeld creates every negative
    // target as a same-user isolated group, so a live member remains signalable.
    return (processId < 0 &&
        error instanceof Error &&
        error.code === "EPERM");
}
function isNoSuchProcessError(error) {
    return (error instanceof Error && error.code === "ESRCH");
}
function reportAgentControllerProcessDiscoveryFailure(input) {
    if (input.reporter) {
        try {
            input.reporter(input.failure);
            return;
        }
        catch (reporterError) {
            reportAgentControllerProcessDiscoveryFailureToConsole({
                ...input.failure,
                reporterError,
            });
            return;
        }
    }
    reportAgentControllerProcessDiscoveryFailureToConsole(input.failure);
}
function reportAgentControllerProcessCleanupFailure(input) {
    if (input.reporter) {
        try {
            input.reporter(input.failure);
            return;
        }
        catch (reporterError) {
            reportAgentControllerProcessCleanupFailureToConsole({
                ...input.failure,
                reporterError,
            });
            return;
        }
    }
    reportAgentControllerProcessCleanupFailureToConsole(input.failure);
}
function reportAgentControllerProcessCleanupFailureToConsole(input) {
    const error = normalizeErrorForDiagnostic(input.error);
    const reporterError = input.reporterError
        ? normalizeErrorForDiagnostic(input.reporterError)
        : null;
    console.warn("[openmeld.agent_controller.process_cleanup_failed]", {
        errorCode: error.code,
        errorMessage: error.message,
        errorName: error.name,
        processId: input.processId,
        ...(reporterError
            ? {
                reporterErrorMessage: reporterError.message,
                reporterErrorName: reporterError.name,
            }
            : {}),
        signal: input.signal,
        source: "remaining_descendants_signal",
    });
}
function reportAgentControllerProcessDiscoveryFailureToConsole(input) {
    processDiscoveryDiagnostics.failure(input);
}
function normalizeErrorForDiagnostic(error) {
    if (error instanceof Error) {
        return {
            code: error.code ?? null,
            message: error.message,
            name: error.name,
        };
    }
    return {
        code: null,
        message: String(error),
        name: "NonError",
    };
}
